import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";
import { useSettings } from "@/lib/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  { label: "How much is a deep clean?", testid: "chat-chip-deep-clean" },
  { label: "Do you cover Epping?", testid: "chat-chip-coverage" },
  { label: "How do I book?", testid: "chat-chip-booking" },
];

export default function ChatWidget() {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello, I'm Sparkle — the Don Sparkle concierge. Ask me anything about our services, prices, hours or coverage.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsContact, setNeedsContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", contact: "" });
  const [contactSent, setContactSent] = useState(false);
  const sessionRef = useRef(
    window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}`
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, needsContact, contactSent, open]);

  const send = async (preset) => {
    const message = (preset ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let pending = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        pending += decoder.decode(value, { stream: true });
        const parts = pending.split("\n\n");
        pending = parts.pop();
        for (const part of parts) {
          if (part.startsWith("data:")) {
            try {
              const payload = JSON.parse(part.slice(5).trim());
              if (payload.text) {
                acc += payload.text;
                const snapshot = acc;
                setMessages((m) => {
                  const c = [...m];
                  c[c.length - 1] = { role: "assistant", text: snapshot };
                  return c;
                });
              }
            } catch {}
          } else if (part.startsWith("event: meta")) {
            const line = part.split("\n").find((l) => l.startsWith("data:"));
            if (line) {
              try {
                const meta = JSON.parse(line.slice(5).trim());
                if (meta.handoff) setNeedsContact(true);
              } catch {}
            }
          }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          text: `Sorry — I'm having trouble connecting. Please call us on ${settings.phone_display}.`,
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/assistant/handoffs`, {
        session_id: sessionRef.current,
        name: contactForm.name,
        contact: contactForm.contact,
      });
      setContactSent(true);
    } catch {
      setContactSent(true);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-6 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-[#0F382A] shadow-[0_20px_40px_-12px_rgba(11,29,22,0.5)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#1B523F]"
        aria-label="Chat with Sparkle"
      >
        {open ? (
          <X className="h-5 w-5 text-[#D4AF37]" />
        ) : (
          <MessageCircle className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
        )}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#D4AF37]" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[80] flex h-[32rem] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-[1.5rem] border border-[#E5DEC3] bg-white shadow-[0_40px_80px_-24px_rgba(11,29,22,0.35)]"
          >
            <div className="flex items-center gap-3 bg-[#0B1D16] px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D4AF37]/15">
                <Sparkles className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#FAF8F5]">Sparkle</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FAF8F5]/50">
                  AI Concierge · replies instantly
                </p>
              </div>
            </div>

            <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto bg-[#FAF8F5] px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-md bg-[#0F382A] text-[#FAF8F5]"
                        : "rounded-bl-md border border-[#E5DEC3] bg-white text-[#121A16]"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}

              {needsContact && !contactSent && (
                <form
                  data-testid="chat-contact-form"
                  onSubmit={sendContact}
                  className="rounded-2xl border border-[#D4AF37]/50 bg-[#F4ECE1] p-4"
                >
                  <p className="text-xs font-semibold text-[#0F382A]">
                    Leave your details — Don will respond personally
                  </p>
                  <input
                    data-testid="chat-contact-name-input"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your name"
                    className="mt-3 w-full rounded-lg border border-[#E5DEC3] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0F382A]"
                  />
                  <input
                    data-testid="chat-contact-input"
                    required
                    value={contactForm.contact}
                    onChange={(e) => setContactForm({ ...contactForm, contact: e.target.value })}
                    placeholder="Phone or email"
                    className="mt-2 w-full rounded-lg border border-[#E5DEC3] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0F382A]"
                  />
                  <button
                    data-testid="chat-contact-submit"
                    type="submit"
                    className="mt-3 w-full rounded-full bg-[#0F382A] py-2.5 text-xs font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#1B523F]"
                  >
                    Request a callback
                  </button>
                </form>
              )}
              {contactSent && (
                <p data-testid="chat-contact-confirmation" className="rounded-2xl border border-[#D4AF37]/50 bg-[#F4ECE1] px-4 py-3 text-xs text-[#0F382A]">
                  Thank you — your details are with Don. Expect a call or email within one working day.
                </p>
              )}
              <div ref={bottomRef} />
            </div>

            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 border-t border-[#E5DEC3] bg-white px-4 pt-3">
                {CHIPS.map((c) => (
                  <button
                    key={c.label}
                    data-testid={c.testid}
                    onClick={() => send(c.label)}
                    className="rounded-full border border-[#E5DEC3] px-3 py-1.5 text-xs text-[#4A5A52] transition-colors duration-300 hover:border-[#0F382A] hover:text-[#0F382A]"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 border-t border-[#E5DEC3] bg-white p-3">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about prices, hours, areas…"
                className="flex-1 rounded-full border border-[#E5DEC3] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-[#0F382A]"
              />
              <button
                data-testid="chat-send-button"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0F382A] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-40"
                aria-label="Send message"
              >
                <Send className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
